import { defaultTheme } from 'react-admin';

const theme = {
    ...defaultTheme,
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        fontSize: 14,
        h1: {
            fontSize: '2rem',
            fontWeight: 700,
            letterSpacing: '-0.02em',
        },
        h2: {
            fontSize: '1.75rem',
            fontWeight: 600,
            letterSpacing: '-0.01em',
        },
        h3: {
            fontSize: '1.5rem',
            fontWeight: 600,
            letterSpacing: '-0.01em',
        },
        h4: {
            fontSize: '1.25rem',
            fontWeight: 600,
        },
        h5: {
            fontSize: '1.125rem',
            fontWeight: 600,
        },
        h6: {
            fontSize: '1rem',
            fontWeight: 600,
        },
        subtitle1: {
            fontSize: '1rem',
            fontWeight: 500,
        },
        subtitle2: {
            fontSize: '0.875rem',
            fontWeight: 500,
        },
        body1: {
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: 1.6,
        },
        body2: {
            fontSize: '0.875rem',
            fontWeight: 400,
            lineHeight: 1.5,
        },
        button: {
            fontSize: '0.875rem',
            fontWeight: 600,
            textTransform: 'none',
        },
        caption: {
            fontSize: '0.75rem',
            fontWeight: 400,
            lineHeight: 1.4,
        },
        overline: {
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
        },
    },
    shape: {
        borderRadius: '12px',
    },
    components: {
        ...defaultTheme.components,
        MuiCssBaseline: {
            styleOverrides: (theme) => ({
                '.rich-text': {
                    '& p:empty': {
                        height: '1em'
                    },
                    '& img': {
                        maxWidth: '100%'
                    }
                },
                '.recharts-wrapper *:focus': {
                    outline: 'none'
                },
                'a > .MuiCard-root, a > .MuiCardActionArea-root': {
                    color: theme.palette.text.primary
                }
            })
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    boxShadow: '0px 1px 30px 1px rgba(0, 0, 0, 0.11)',
                    zIndex: 1300
                }
            }
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    boxShadow: 'none',
                    backgroundImage: 'none'
                },
                elevation1: ({ theme }) => ({
                    border: `1px solid ${theme.palette.divider}`,
                }),
                elevation8: ({ theme }) => ({
                    border: `1px solid ${theme.palette.divider}`,
                }),
            },
        },
        MuiList: {
            styleOverrides: {
                root: {
                    padding: 0
                }
            }
        },
        MuiDrawer: {
            styleOverrides: {
                docked: {
                    marginRight: '19px',
                    boxShadow: 'none !important',
                    '& .footer-menu': {
                        display: 'none'
                    },
                    '& .RaMenu-open': {
                        height: 'calc(100% - 48px)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflowY: 'scroll',
                        scrollbarWidth: 'none',
                        '& .footer-menu': {
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            margin: '5px',
                            justifySelf: 'flex-end',
                            alignSelf: 'center',
                            marginTop: 'auto'
                        }
                    },
                    '& .RaMenu-closed': {
                        width: '55px !important'
                    }
                }
            }
        },
        RaSidebar: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '& .RaSidebar-fixed': {
                        boxShadow: 'none !important'
                    },
                    '& .RaSidebar-paperAnchorLeft .RaSidebar-fixed': {
                        borderRight: `1px solid ${theme.palette.divider} !important`
                    },
                    '& .MuiMenuItem-root': {
                        borderRadius: '6px',
                        margin: '2px 8px',
                        padding: '6px 8px',
                        fontSize: '15px'
                    }
                })
            }
        },
        MuiFormControl: {
            defaultProps: {
                variant: 'outlined',
                size: 'small'
            },
            styleOverrides: {
                root: {
                    '.RaCreate-main &, .RaEdit-main &': {
                        minWidth: '250px !important'
                    }
                },
                fullWidth: {
                    maxWidth: '50%',
                    '@media (max-width: 600px)': {
                        maxWidth: '100%'
                    }
                }
            }
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    '.RaCreate-main &, .RaEdit-main &': {
                        minWidth: '250px !important'
                    }
                }
            }
        },
        RaSelectInput: {
            styleOverrides: {
                root: {
                    '.RaCreate-main &, .RaEdit-main &': {
                        minWidth: '250px !important'
                    }
                }
            }
        },
        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
                size: 'small'
            },
        },
        MuiSnackbarContent: {
            styleOverrides: {
                message: {
                    whiteSpace: 'pre-wrap'
                }
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    '@media (min-width: 600px)': {
                        minHeight: '36px'
                    }
                }
            }
        },
        MuiInputBase: {
            defaultProps: {
                autoComplete: 'off'
            }
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    boxShadow: 'none',
                    borderRadius: '12px'
                }
            }
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    borderBottom: '1px solid #DADCE0',
                },
                title: {
                    fontSize: '22px'
                }
            }
        },
        RaRichTextInput: {
            styleOverrides: {
                root: {
                    maxWidth: '100%',
                    '& .RaRichTextInput-editorContent': {
                        '& > div': {
                            minHeight: '200px',
                        }
                    },
                    '& .RaRichTextInput-editorContent img': {
                        maxWidth: '100%'
                    },
                    '& .RaRichTextInputToolbar-root': {
                        alignSelf: 'flex-start'
                    },
                    '& .ProseMirror': {
                        borderColor: '#000 !important'
                    },
                    '& .tiptap': {
                        'ul[data-type="taskList"]': {
                            listStyle: 'none',
                            marginLeft: 0,
                            padding: 0,

                            'li': {
                                alignItems: 'flex-start',
                                display: 'flex',

                                '> label': {
                                    flex: '0 0 auto',
                                    marginRight: '0.5rem',
                                    userSelect: 'none'
                                },

                                '> div': {
                                    flex: '1 1 auto'
                                }
                            },

                            'input[type="checkbox"]': {
                                cursor: 'pointer'
                            },

                            'ul[data-type="taskList"]': {
                                margin: 0
                            }
                        }
                    }
                }
            }
        },
        MuiTypography: {
            styleOverrides: {
                root: {
                    '& ul[data-type="taskList"]': {
                        listStyle: 'none',
                        marginLeft: 0,
                        padding: 0,
                        pointerEvents: 'none',

                        'li': {
                            alignItems: 'flex-start',
                            display: 'flex',

                            '> label': {
                                flex: '0 0 auto',
                                marginRight: '0.5rem',
                                userSelect: 'none'
                            },

                            '> div': {
                                flex: '1 1 auto'
                            },
                            '& p': {
                                margin: 0
                            }
                        },

                        'input[type="checkbox"]': {
                            cursor: 'pointer'
                        },

                        'ul[data-type="taskList"]': {
                            margin: 0
                        }
                    }
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                    letterSpacing: '0.01em'
                },
                contained: {
                    boxShadow: 'none',
                    '&:hover, &:active': {
                        boxShadow: 'none'
                    }
                }
            }
        },
        RaDatagrid: {
            styleOverrides: {
                root: {
                    '& tbody .MuiTableRow-root:last-child .MuiTableCell-root': {
                        border: 0
                    }
                }
            }
        },
        RaInPlaceEditor: {
            styleOverrides: {
                root: {
                    minHeight: '20px',
                    '& .RaInPlaceEditor-reading': {
                        minHeight: 'inherit'
                    },
                    '& .RaInPlaceEditor-reading div, & .RaInPlaceEditor-editing input, & .RaInPlaceEditor-editing textarea, & .RaInPlaceEditor-saving div': {
                        fontSize: '0.875rem',
                        marginBottom: 0
                    }
                }
            }
        },
        RaList: {
            styleOverrides: {
                root: {
                    '& .RaList-actions': {
                        marginBottom: '8px',
                        paddingLeft: '1px'
                    }
                }
            }
        },
        RaBulkDeleteWithConfirmButton: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        color: '#FFF'
                    }
                }
            }
        },
        RaBulkActionsToolbar: {
            styleOverrides: {
                root: {
                    paddingBottom: '8px',
                    '& .RaBulkActionsToolbar-icon:hover': {
                        color: '#FFF'
                    }
                }
            }
        }
    }
};

export default theme;
